/**
 * How long something took, said the way a person would say it.
 *
 * Every elapsed figure in the chat used to be bare seconds, which is fine while a turn is short
 * and stops being readable the moment it is not: `155s` is a number the reader has to divide
 * before it means anything, and the numbers that need reading are exactly the long ones — the
 * turn someone is sitting and waiting on. A minute is where the change happens, so a minute is
 * where the format changes.
 *
 * Sub-minute output is untouched, deliberately: the callers disagree about it (a tool timing is
 * worth a decimal, a ticking wait is not) and none of them was wrong. What they share is the
 * part above a minute, and that is the whole of what lives here.
 */

/** @desc The point a bare seconds count stops reading as a duration. */
export const SECONDS_PER_MINUTE = 60;

/** @desc The same threshold in milliseconds, for the callers that measure in them. */
export const MINUTE_MS = SECONDS_PER_MINUTE * 1000;

/**
 * @desc Whole seconds as `45s` under a minute and `1m 21s` at or above it.
 *
 * Unpadded on both halves: `2m 0s` rather than `2m 00s`, because this is a sentence about a wait
 * and not a stopwatch face — a leading zero implies a precision the surrounding figures do not
 * have.
 */
export function formatElapsedSeconds(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  if (whole < SECONDS_PER_MINUTE) return `${whole}s`;

  const minutes = Math.floor(whole / SECONDS_PER_MINUTE);
  return `${minutes}m ${whole % SECONDS_PER_MINUTE}s`;
}
